import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, WorkflowStepInterface } from '@vendasta/automata';
import { combineLatest, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { TaskService } from './../../../../data-services/task.service';
import { ASSIGNED_SALESPERSON, getFormattedDisplayName, UsersService } from './../../../../data-services/users.service';
import { CommonDisplayActionService } from '../../common/common-display-action.service';
import { CommonDisplayActivityService } from '../../common/common-display-activity.service';
import { AutomationErrorHandlerService } from '../../../services';

/**
 * <h1> IMPORTANT </h1>
 *
 * Do not implement new actions, triggers, and filters using {@link CommonDisplayActionService}
 * nor {@link CommonDisplayActivityService}. These classes are deprecated, retained for backward
 * compatibility only, and <strong> are likely to be removed in the future. </strong>
 * Instead, subclass from {@link AutomationDisplayService} as this implements all modern interfaces,
 * provides convenience methods to make code more concise and readable,
 * and provides adapters to the old system while it is being phased out.
 * @see AutomationDisplayService
 * @example AddTagService
 */
@Injectable({ providedIn: 'root' })
export class CreateSalesTaskService implements CommonDisplayActivityService, CommonDisplayActionService {
  constructor(
    private readonly translateService: TranslateService,
    public sanitizer: DomSanitizer,
    private readonly usersService: UsersService,
    private readonly taskService: TaskService,
    private readonly errorHandler: AutomationErrorHandlerService,
  ) {}

  getActivitySubtitle(_namespace: string, activity: ActivityInterface): Observable<string> {
    const stepData = activity?.workflowStep?.data ? JSON.parse(activity.workflowStep.data) : {};
    const activityData = activity?.activityData ? JSON.parse(activity.activityData) : {};
    const stepUserIds = stepData?.assignees || [];
    const stepUserCount = stepUserIds.length;
    const actualUserIds = activityData?.user_ids || [];
    const actualUserCount = actualUserIds.length;

    if (actualUserCount === 0 && stepUserCount === 1 && stepUserIds[0] === ASSIGNED_SALESPERSON) {
      return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CREATE_SALES_TASK.ACTIVITY.NO_ASSIGNED_FOUND');
    }

    return of('');
  }

  getActivityTitle(_namespace: string, activity: ActivityInterface): Observable<string> {
    const activityData = activity?.activityData ? JSON.parse(activity.activityData) : {};
    const taskId = activityData.task_id || '';
    const userIds = activityData.user_ids ? activityData.user_ids : [];
    const assignee = userIds[0] || '';

    let name$ = of('');
    if (!!assignee && assignee !== ASSIGNED_SALESPERSON) {
      name$ = this.usersService.getUsersById([assignee]).pipe(
        take(1),
        map((users) => users.map((u) => [u.firstName, u.lastName].join(' '))),
        map((userNames) => {
          return userNames[0];
        }),
      );
    }

    let task$ = of({ title: '' });
    if (taskId) {
      task$ = this.taskService.getTask(activity.entityId, taskId).pipe(take(1));
    }

    return combineLatest([name$, task$]).pipe(
      switchMap(([name, t]) => {
        if (name) {
          return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CREATE_SALES_TASK.ACTIVITY.TO_SINGLE_USER', {
            taskName: t.title,
            name: name,
          });
        }
        return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CREATE_SALES_TASK.ACTIVITY.DID_NOT_CREATE');
      }),
    );
  }

  getTemplateActionSubtitle(namespace: string, step: WorkflowStepInterface): Observable<string> {
    return this.getActionSubtitle(namespace, step);
  }

  getActionSubtitle(_namespace: string, step: WorkflowStepInterface): Observable<string> {
    const data = step ? JSON.parse(step.data) : {};

    if (data.assignees?.length > 0 && data.assignees[0] !== '') {
      return this.getActionSubtitleSuccessState(data).pipe(
        catchError((err) => {
          console.error(err);
          return this.getActionSubtitleErrorState(data, step);
        }),
      );
    }

    return this.getActionSubtitleEmptyState(step);
  }

  getActionSubtitleSuccessState(data: any): Observable<string> {
    if (data.assignees[0] === 'ASSIGNED_SALESPERSON') {
      return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CREATE_SALES_TASK.ACTION.TO_ASSIGNED_SALESPERSON', {
        taskName: data.title,
      });
    }
    return this.usersService.getUsersById(data.assignees).pipe(
      take(1),
      map((users) => getFormattedDisplayName(users[0])),
      switchMap((salespersonName) => {
        if (!salespersonName) {
          return throwError("Salesperson name wasn't found");
        }

        return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CREATE_SALES_TASK.ACTION.TO_SINGLE_USER', {
          taskName: data.title,
          name: salespersonName,
        });
      }),
    );
  }

  getActionSubtitleErrorState(data: any, step: WorkflowStepInterface): Observable<string> {
    this.errorHandler.setValidationError({
      stepId: step?.id,
      taskDefinitionId: step?.taskDefinitionId,
      errorMessage: this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.CREATE_SALES_TASK.CONFIG_ERROR'),
      isNonDismissable: true,
    });
    return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CREATE_SALES_TASK.CREATE_SALES_TASK');
  }

  getActionSubtitleEmptyState(step: WorkflowStepInterface): Observable<string> {
    this.errorHandler.setValidationError({
      stepId: step?.id,
      taskDefinitionId: step?.taskDefinitionId,
      errorMessage: this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.CREATE_SALES_TASK.CONFIG_ERROR'),
      isNonDismissable: true,
    });
    return this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CREATE_SALES_TASK.CREATE_SALES_TASK');
  }
}
